import { useEffect, useState } from "react";
import { useNavigate, useSearchParams} from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { Sparkles, Check, ChevronsUpDown, ClipboardList } from "lucide-react";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00", "23:00"];

export default function BookAppointment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isStaff = norm(user.roleName) === "admin" || norm(user.roleName) === "staff" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";
  const [services, setServices] = useState([]);

  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState();
  const [customerOpen, setCustomerOpen] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [serviceOpen, setServiceOpen] = useState(false);
  const [staffId, setStaffId] = useState("");
  const [staffHours, setStaffHours] = useState(null);
  const [walkIn, setWalkIn] = useState(false);
  const [date, setDate] = useState(() => {
    const now = new Date();
    const d = searchParams.get("date");
    if (d) {
      const p = new Date(d + "T00:00:00");
      if (!isNaN(p)) return p;
    }
    return now;
  });
  const [time, setTime] = useState(() => searchParams.get("time") || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);
  const [holidays, setHolidays] = useState([]);

  // Prefill staff from URL query once staff list loads
  useEffect(() => {
    const qStaff = searchParams.get("staff");
    if (qStaff && staff.length && !staffId && staff.some((s) => s.id === qStaff)) {
      setStaffId(qStaff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff]);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([api.get("/services"), api.get("/staff/bookable")]);
        setServices(s.data);
        setStaff(st.data);
         if (isStaff) {
          const c = await api.get("customers");
          setCustomers(c.data);
        }
      } catch { toast.error("Failed to load options"); }
    })();
   }, [isStaff]);

  // Fetch staff working hours when staff is selected
  useEffect(() => {
    if (!staffId) { setStaffHours(null); setLeaveDates([]); setHolidays([]); return; }
    api.get(`/staff/${staffId}/hours`)
      .then((r) => {
        const data = r.data || {};
        // If response is an array, it's just the schedule
        if (Array.isArray(data)) {
          const mapped = data.reduce((acc, item) => ({ ...acc, [item.dayOfWeek]: item }), {});
          setStaffHours(mapped);
          setLeaveDates([]);
          setHolidays([]);
        } else {
          // Response is an object with schedule, leaveDates, holidays
          const schedule = data.schedule || data.hours || data;
          if (Array.isArray(schedule)) {
            setStaffHours(schedule.reduce((acc, item) => ({ ...acc, [item.dayOfWeek]: item }), {}));
          } else {
            setStaffHours(schedule);
          }
          setLeaveDates(data.leaveDates || []);
          setHolidays(data.holidays || []);
        }
      })
      .catch(() => { setStaffHours(null); setLeaveDates([]); setHolidays([]); });
  }, [staffId]);

  // Fetch existing appointments for this staff + date to block occupied slots
  useEffect(() => {
    if (!staffId || !date) { setBookedSlots([]); return; }
    const dateStr = format(date, "yyyy-MM-dd");
    api.get(`/staff/${staffId}/availability?date=${dateStr}`)
      .then((r) => {
        // Expects array of booked appointments with startTime and durationMinutes/endTime
        const data = Array.isArray(r.data) ? r.data : r.data?.bookedSlots || r.data?.appointments || [];
        setBookedSlots(data);
      })
      .catch(() => setBookedSlots([]));
  }, [staffId, date]);

  const DAYS_MAP = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

  // Get the schedule for the selected date
  const selectedDaySchedule = staffHours
    ? staffHours[DAYS_MAP[date.getDay()]]
    : null;

  // Check if staff is working on selected date
  const isStaffOff = selectedDaySchedule ? !selectedDaySchedule.working : false;

  // Check if a time slot is occupied by an existing booking
  const isSlotBooked = (slot) => {
    return bookedSlots.some((appt) => {
      const apptStart = (appt.startTime || "").slice(0, 5);
      const durationMin = appt.durationMinutes || 60;
      // Calculate end time from start + duration
      const [h, m] = apptStart.split(":").map(Number);
      const startMinutes = h * 60 + m;
      const endMinutes = startMinutes + durationMin;
      const [slotH, slotM] = slot.split(":").map(Number);
      const slotMinutes = slotH * 60 + slotM;
      return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    });
  };

  // Filter time slots based on staff working hours and disable past hours for today
  const availableSlots = TIME_SLOTS.filter((t) => {
    if (!selectedDaySchedule || !selectedDaySchedule.working) return false;
    const startTime = selectedDaySchedule.startTime ? selectedDaySchedule.startTime.slice(0, 5) : "00:00";
    const endTime = selectedDaySchedule.endTime ? selectedDaySchedule.endTime.slice(0, 5) : "23:59";
    if (t < startTime || t > endTime) return false;
    return true;
  });

  // Check if a date should be disabled (staff is off, on leave, or holiday)
  const isDateDisabled = (d) => {
    // Past dates are allowed for booking.
    // Check leave dates and holidays
    const dateStr = format(d, "yyyy-MM-dd");
    if (leaveDates.includes(dateStr)) return true;
    if (holidays.includes(dateStr)) return true;
    // Check working schedule
    if (!staffHours) return false;
    const daySchedule = staffHours[DAYS_MAP[d.getDay()]];
    return daySchedule ? !daySchedule.working : false;
  };

  const submit = async () => {
    if (!serviceId || !staffId) { toast.error("Please select a service and stylist"); return; }
    if (!walkIn && !time) { toast.error("Please select a time slot"); return; }
    if (isStaff && !customerId) { toast.error("Please select a customer"); return; }
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        serviceId: serviceId,
        staffId: staffId,
        customerId: isStaff ? customerId : user.id,
        appointmentDate: format(date, "yyyy-MM-dd"),
        startTime: time,
        remarks: notes,
        walkIn: walkIn,
      });
      toast.success("Appointment booked");
      // If we came from Calendar, redirect back there; otherwise go to appointments
      if (searchParams.get("date") || searchParams.get("time")) {
        navigate("/dashboard/calendar");
      } else {
        navigate("/dashboard/appointments");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  const selectedService = services.find((s) => s.id === serviceId);

  return (
    <div className="space-y-8" data-testid="book-appointment-page">
      <div>
        <div className="overline mb-2">New Reservation</div>
        
        <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-emerald-500" /> Book Appointment
        </h1>
        <p className="text-emerald-muted mt-2 text-sm">Select a service, stylist, and time that suits you.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          {isStaff && (
            <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6 flex items-center justify-between">
              <div>
                <div className="overline">Walk-In</div>
                <p className="text-xs text-emerald-muted mt-1">Skip date/time selection for immediate service</p>
              </div>
              <Switch checked={walkIn} onCheckedChange={(v) => {
                setWalkIn(v);
                if (v) {
                  const now = new Date();
                  setDate(now);
                  const h = String(now.getHours()).padStart(2, "0");
                  const m = now.getMinutes() < 30 ? "00" : "30";
                  setTime(`${h}:${m}`);
                }
              }} />
            </div>
          )}
          {isStaff && (
            <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
              <div className="overline mb-3">Customer</div>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="customer-typeahead-trigger"
                    className="w-full h-12 flex items-center justify-between bg-emerald-elev border border-gold-subtle rounded-md px-3 text-left text-white hover:border-gold transition"
                  >
                    <span className={customerId ? "text-white" : "text-emerald-muted"}>
                      {customerId
                        ? (() => {
                            const c = customers.find((x) => x.id === customerId);
                            return c ? `${c.firstName} ${c.lastName || ""} — ${c.phone || c.email}` : "Select customer";
                          })()
                        : "Search by name or phone…"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-emerald-muted opacity-70" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-0 w-[--radix-popover-trigger-width] bg-emerald-surface border-gold-subtle"
                >
                  <Command
                    className="bg-emerald-surface text-white"
                    filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
                  >
                    <CommandInput
                      data-testid="customer-typeahead-input"
                      placeholder="Type name, email, or phone…"
                      className="text-white"
                    />
                    <CommandList>
                      <CommandEmpty className="py-4 text-center text-sm text-emerald-muted">No customers found.</CommandEmpty>
                        <CommandGroup>
                            {customers.map((c) => (
                              <CommandItem
                                      key={c.id}
                                      value={`${c.firstName || ""} ${c.lastName || ""} ${c.phone || ""}`}
                                      data-testid={`customer-option-${c.id}`}
                                      onSelect={() => { setCustomerId(c.id); setCustomerOpen(false); }}
                                      className="text-white cursor-pointer aria-selected:bg-[rgba(197,160,89,0.15)] aria-selected:text-gold">
                                      <Check className={`mr-2 h-4 w-4 ${customerId === c.id ? "opacity-100 text-gold" : "opacity-0"}`} />
                                      <div className="flex flex-col">
                                        <span className="text-sm">{c.firstName} {c.lastName || ""}</span>
                                        <span className="text-xs text-emerald-muted">{c.phone || "—"}</span>
                                      </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    </Command>
                    </PopoverContent>
                  </Popover>
                  {(() => {
                    const selCustomer = customers.find((x) => x.id === customerId);
                    if (!selCustomer) return null;
                    return (
                      <>
                        {(selCustomer.numberOfVisits || 0) > 4 && (
                          <p className="text-xs text-red-400 font-medium mt-2">Repeated Customer</p>
                        )}
                        {selCustomer.notOnWhatsApp && (
                          <p className="text-xs text-red-500 font-medium mt-2" data-testid="customer-not-on-whatsapp">
                            customer not on whats app
                          </p>
                        )}
                      </>
                    );
                  })()}
                  <p className="text-xs text-emerald-muted mt-3">Search by name or phone.</p>
            </div>
          )}
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-3">Service</div>
            <Popover open={serviceOpen} onOpenChange={setServiceOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="service-select"
                  className="w-full h-12 flex items-center justify-between bg-emerald-elev border border-gold-subtle rounded-md px-3 text-left text-white hover:border-gold transition"
                >
                  <span className={serviceId ? "text-white" : "text-emerald-muted"}>
                    {serviceId
                      ? (() => {
                          const s = services.find((x) => x.id === serviceId);
                          return s ? `${s.name} — ${formatINR(s.price)}` : "Choose a service";
                        })()
                      : "Search by service name…"}
                  </span>
                  <ChevronsUpDown className="w-4 h-4 text-emerald-muted opacity-70" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] bg-emerald-surface border-gold-subtle">
                <Command className="bg-emerald-surface text-white" filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                  <CommandInput placeholder="Type service name…" className="text-white" />
                  <CommandList>
                    <CommandEmpty className="py-4 text-center text-sm text-emerald-muted">No services found.</CommandEmpty>
                    <CommandGroup>
                      {services.map((s) => (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${s.category || ""}`}
                          data-testid={`service-option-${s.id}`}
                          onSelect={() => { setServiceId(s.id); setServiceOpen(false); }}
                          className="text-white cursor-pointer aria-selected:bg-[rgba(197,160,89,0.15)] aria-selected:text-gold"
                        >
                          <Check className={`mr-2 h-4 w-4 ${serviceId === s.id ? "opacity-100 text-gold" : "opacity-0"}`} />
                          <div className="flex flex-col">
                            <span className="text-sm">{s.name}</span>
                            <span className="text-xs text-emerald-muted">{formatINR(s.price)} · {s.durationMinutes}min</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedService && (
              <p className="text-xs text-emerald-muted mt-3">{selectedService.description}</p>
            )}
          </div>

          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-3">Stylist</div>
            <Select value={staffId} onValueChange={(v) => { setStaffId(v); setTime(""); }}>
              <SelectTrigger data-testid="staff-select" className="bg-emerald-elev border-gold-subtle text-white h-12"><SelectValue placeholder="Choose a stylist" /></SelectTrigger>
              <SelectContent className="bg-emerald-surface border-gold-subtle text-white">
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`staff-option-${s.id}`}>{s.firstName + " " + (!!s.lastName ? s.lastName : '')} — {s.designation}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-3">Notes (optional)</div>
            <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-emerald-elev border-gold-subtle text-white" placeholder="Any preferences we should know?" />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-3">Date</div>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { if (d) { setDate(d); setTime(""); setBookedSlots([]); } }}
                disabled={isDateDisabled}
                className="text-white"
                data-testid="booking-calendar"
              />
            </div>
          </div>

          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-3">Time</div>
            {!staffId && <p className="text-sm text-emerald-muted">Select a stylist first to see available times.</p>}
            {staffId && isStaffOff && <p className="text-sm text-red-300">Selected stylist is off on this day. Please choose another date.</p>}
            {staffId && !isStaffOff && bookedSlots.length > 0 && (
              <p className="text-xs text-emerald-muted mb-3 text-white">Faded slots are already booked for this stylist.</p>
            )}
            {staffId && !isStaffOff && availableSlots.length === 0 && <p className="text-sm text-emerald-muted">No available time slots for this day.</p>}
            {staffId && !isStaffOff && availableSlots.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {availableSlots.map((t) => {
                  const booked = isSlotBooked(t);
                  return (
                    <button
                      key={t}
                      data-testid={`time-slot-${t}`}
                      disabled={booked}
                      onClick={() => !booked && setTime(t)}
                      className={`py-2 rounded-md text-sm border transition ${
                        time === t
                          ? "bg-gold text-emerald-950 border-gold"
                          : booked
                          ? "bg-emerald-elev/30 text-emerald-muted/40 border-gold-subtle/30 cursor-not-allowed opacity-40"
                          : "bg-emerald-elev text-emerald-muted border-gold-subtle hover:text-gold hover:border-gold"
                      }`}
                      style={time === t ? { background: "#C5A059", color: "#0A1F14" } : {}}
                    >{t}</button>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={submit} disabled={submitting} data-testid="submit-booking-btn"
            className="gold-button w-full h-14 rounded-md flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? "Booking…" : (<><Sparkles className="w-4 h-4" /> Confirm Booking</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
